import cartImg from '@assets/landing/section3/cart.png';
import invoiceMainImg from '@assets/landing/section3/invoices_img.png';
import { Box } from '@components/ui/atoms/Box';
import { Button } from '@components/ui/atoms/Button';
import { Container } from '@components/ui/atoms/Container';
import { Flex } from '@components/ui/atoms/Flex';
import { Heading } from '@components/ui/atoms/Heading';
import { Image } from '@components/ui/atoms/Image';
import { Text } from '@components/ui/atoms/Text';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { fadeUp, floatHover15, slideInCustom } from '../../../shared/utils/animationsUtil';
import { useScrollReveal } from '../../../shared/utils/useScrollReveal';
import { loginPath } from '../auth/urls';

interface CardRowProps {
  label: string;
  value: string;
  isBold?: boolean;
}

export const ServicesSection: React.FC = () => {
  const { isVisible, ref: sectionRef } = useScrollReveal(0.1);

  const CardRow: React.FC<CardRowProps> = ({ label, value, isBold }) => (
    <Flex justify="space-between" mb="0.75rem">
      <Text color="gray.500" fontSize="sm">
        {label}
      </Text>
      <Text color="gray.800" fontWeight={isBold ? '700' : '600'} fontSize="sm">
        {value}
      </Text>
    </Flex>
  );

  return (
    <Box id="services" py="4rem" position="relative" bg="white" overflow="hidden" ref={sectionRef}>
      <Container maxW="container.xl">
        {/* Section Header */}
        <Flex direction="column" mb="8rem">
          <Flex direction="row" justify="center" align="center" gap="0.75rem" mb="3rem">
            <Box h="18px" w="4px" bg="primary" opacity="0.5" />
            <Text fontSize="lg" fontWeight="700" color="gray.800">
              The Services We Provide
            </Text>
          </Flex>

          <Flex w="full" align="center" justify="flex-end" gap="2rem">
            <Box flex="1" h="1px" bg="gray.100" />
            <Text
              fontSize="xs"
              fontWeight="500"
              color="gray.400"
              letterSpacing="0.1em"
              whiteSpace="nowrap"
            >
              GROW YOUR REVENUE{' '}
              <Text as="span" color="gray.800" fontWeight="700">
                FARTHER, FASTER
              </Text>
            </Text>
          </Flex>
        </Flex>

        {/* Service 1: Create Your Own Invoices */}
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          gap="6rem"
          align="center"
          mb={{ base: '6rem', md: '12rem' }}
        >
          {/* Left: Image with floating card */}
          <Box flex="1" position="relative">
            {/* Decorative Background Square */}
            <Box
              position="absolute"
              top={{ base: '15%', md: '25%' }}
              left="-9%"
              transform="translateY(-50%)"
              w={{ base: '300px', md: '450px' }}
              h={{ base: '300px', md: '500px' }}
              bg="rgb(212 255 237 / 60%)"
              borderRadius="3xl"
              zIndex="0"
              opacity={isVisible ? 1 : 0}
              animation={isVisible ? `${fadeUp} 0.8s ease-out forwards` : 'none'}
            />

            <Box
              position="relative"
              zIndex="1"
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="2xl"
              opacity={isVisible ? 1 : 0}
              animation={isVisible ? `${slideInCustom} 1s ease-out forwards` : 'none'}
            >
              <Image
                src={invoiceMainImg}
                alt="Person working on laptop"
                w={{ base: '350px', md: 'full' }}
              />
            </Box>

            {/* Floating Invoice Card */}
            <Box
              position="absolute"
              top={{ base: '160px', md: '50px' }}
              right={{ base: '-10px', md: '-40px' }}
              zIndex="2"
              bg="white"
              p="1.5rem"
              borderRadius="xl"
              boxShadow="xl"
              w="280px"
              opacity={0} // Start invisible for animation
              backdropFilter="blur(2xl)"
              animation={
                isVisible
                  ? `${slideInCustom} 0.8s ease-out forwards 0.4s, ${floatHover15} 6s ease-in-out infinite 1.2s`
                  : 'none'
              }
            >
              <Box mb="1rem">
                <Text fontStyle="italic" fontSize="xs" color="gray.400" mb="0.25rem">
                  Bill to
                </Text>
                <Text fontWeight="700" color="gray.800">
                  Justin Siphron
                </Text>
                <Text fontSize="xs" color="gray.400">
                  justinsiphron@gmail.com
                </Text>
              </Box>

              <Box borderTop="1px solid" borderColor="gray.200" pt="0.75rem">
                <Flex justify="space-between" mb="0.5rem">
                  <Text fontSize="xs" color="gray.600">
                    Farm Stay Overnight
                  </Text>
                  <Text fontSize="xs" fontWeight="600" color="gray.800">
                    $24.99
                  </Text>
                </Flex>
                <Flex justify="space-between" mb="0.75rem">
                  <Text fontSize="xs" color="gray.600">
                    Dinner
                  </Text>
                  <Text fontSize="xs" fontWeight="600" color="gray.800">
                    $10.68
                  </Text>
                </Flex>
                <Flex
                  justify="space-between"
                  borderTop="1px solid"
                  borderColor="gray.200"
                  pt="0.75rem"
                >
                  <Text fontSize="xs" fontWeight="700" color="gray.800">
                    Total
                  </Text>
                  <Text fontSize="xs" fontWeight="800" color="gray.800">
                    $35.57
                  </Text>
                </Flex>
              </Box>
            </Box>
          </Box>

          {/* Right: Text */}
          <Box
            flex="1"
            opacity={isVisible ? 1 : 0}
            animation={isVisible ? `${fadeUp} 0.8s ease-out forwards 0.2s` : 'none'}
          >
            <Heading fontSize="2xl" fontWeight="800" color="gray.800" mb="1.5rem">
              Create Your Own Invoices
            </Heading>
            <Text color="gray.500" fontSize="md" lineHeight="1.8" mb="2.5rem" maxW="450px">
              Create professional invoices in minutes and send them directly to your customers.
              Customize invoice details, track payments, and manage billing from one intuitive
              platform. Simplify your invoicing workflow while ensuring accuracy, consistency, and a
              seamless payment experience for your customers.
            </Text>
            <RouterLink to={loginPath}>
              <Button visual="primary" size="lg" px="2.5rem" borderRadius="md">
                Get Started
              </Button>
            </RouterLink>
          </Box>
        </Flex>

        {/* Service 2: Integrate With Your Website */}
        <Flex direction={{ base: 'column-reverse', lg: 'row' }} gap="6rem" align="center">
          {/* Left: Text */}
          <Box
            flex="1"
            opacity={isVisible ? 1 : 0}
            animation={isVisible ? `${fadeUp} 0.8s ease-out forwards 0.4s` : 'none'}
          >
            <Heading fontSize="2xl" fontWeight="800" color="gray.800" mb="1.5rem">
              Integrate With Your Website
            </Heading>
            <Text color="gray.500" fontSize="md" lineHeight="1.8" maxW="450px">
              Add payments to your website quickly using our prebuilt Checkout and embedded
              components. Create a smooth, fast, and reliable payment experience that looks polished
              and performs seamlessly across devices. With flexible integration options and multiple
              ready-to-use components, you can build, launch, and scale faster—without heavy
              development work.
            </Text>
          </Box>

          {/* Right: Large Invoice card details */}
          <Box flex="1.2" position="relative" w="full">
            {/* Decorative Background Shapes */}
            <Box
              position="absolute"
              top={{ base: '-20%', md: '-8%' }}
              left={{ base: 'auto', md: '-16%' }}
              right={{ base: '16%', md: 'auto' }}
              w={{ base: '60px', md: '90px' }}
              h={{ base: '60px', md: '90px' }}
              bg="cyan.100"
              borderRadius="2xl"
              zIndex="0"
              opacity={isVisible ? 1 : 0}
              animation={
                isVisible
                  ? `${fadeUp} 0.8s ease-out forwards 0.6s, ${floatHover15} 7s ease-in-out infinite`
                  : 'none'
              }
            />
            <Box
              position="absolute"
              top={{ base: '-8%', md: '14px' }}
              left={{ base: 'auto', md: '14px' }}
              right={{ base: '14px', md: 'auto' }}
              w={{ base: '40px', md: '60px' }}
              h={{ base: '40px', md: '60px' }}
              bg="rgb(212 255 237 / 80%)"
              borderRadius="xl"
              zIndex="0"
              opacity={isVisible ? 1 : 0}
              animation={
                isVisible
                  ? `${fadeUp} 0.8s ease-out forwards 0.7s, ${floatHover15} 6s ease-in-out infinite`
                  : 'none'
              }
            />
            <Box
              position="absolute"
              bottom="16%"
              left={{ base: '-20px', md: '-40px' }}
              w={{ base: '240px', md: '360px' }}
              h={{ base: '240px', md: '360px' }}
              bg="cyan.50"
              borderRadius="3xl"
              zIndex="0"
              opacity={isVisible ? 1 : 0}
              animation={isVisible ? `${fadeUp} 0.8s ease-out forwards 0.3s` : 'none'}
            />

            {/* Main UI Card */}
            <Box
              bg="#ffffff80"
              borderRadius="2xl"
              p={{ base: '1.5rem', md: '3rem' }}
              boxShadow="0 10px 40px rgba(0,0,0,0.06)"
              border="2px solid"
              borderColor="#e8e1ff"
              position="relative"
              zIndex="1"
              margin={{ base: '8px', md: '30px' }}
              opacity={isVisible ? 1 : 0}
              animation={isVisible ? `${fadeUp} 0.8s ease-out forwards 0.5s` : 'none'}
            >
              <Box mb={{ base: '1.5rem', md: '2.5rem' }} pr={{ base: '70px', md: '0' }}>
                <Text
                  color="gray.500"
                  fontWeight="600"
                  fontSize={{ base: 'xs', md: 'sm' }}
                  mb="0.5rem"
                >
                  INVOICE ID: BT-9E6FF7
                </Text>
                <Heading
                  fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                  fontWeight="800"
                  color="gray.800"
                  mb="0.5rem"
                  lineHeight="1.2"
                >
                  $1,122.11 USD
                </Heading>
                <Text color="gray.500" fontSize={{ base: 'xs', md: 'sm' }}>
                  CREATED: 12 / 20 / 2023
                </Text>
              </Box>

              <Box
                mb={{ base: 'auto', md: '1rem' }}
                borderBottom={'1px solid'}
                borderColor="gray.200"
                pb={{ base: '1rem', md: '2rem' }}
              >
                <Flex gap="1rem" mb="4px">
                  <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>
                    From
                  </Text>
                  <Text color="gray.700" fontWeight="700" fontSize={{ base: 'xs', md: 'sm' }}>
                    Unicorn Travels
                  </Text>
                </Flex>
                <Flex gap="1rem" mb="4px">
                  <Text color="gray.400" fontSize={{ base: 'xs', md: 'sm' }}>
                    To
                  </Text>
                  <Text color="gray.700" fontWeight="700" fontSize={{ base: 'xs', md: 'sm' }}>
                    Hotel Lake View
                  </Text>
                </Flex>
              </Box>
              <Box mb={{ base: 'auto', md: '2rem' }}>
                <Flex
                  justify="space-between"
                  align="center"
                  mb="1rem"
                  py="1rem"
                  borderTop="1px solid"
                  borderColor="gray.50"
                >
                  <Text fontWeight="700" color="gray.800" fontSize={{ base: 'sm', md: 'md' }}>
                    View invoice details
                  </Text>
                  <Box transform="rotate(-90deg)">
                    <Text fontSize="xl" color="gray.400">
                      ›
                    </Text>
                  </Box>
                </Flex>
                <Flex justify="space-between" mb="1rem">
                  <Text color="gray.500" fontSize={{ base: 'xs', md: 'sm' }}>
                    Description
                  </Text>
                </Flex>
                <Flex
                  direction="column"
                  gap="0.5rem"
                  mb="2rem"
                  borderBottom={'1px solid'}
                  borderColor="gray.200"
                  pb={{ base: '1rem', md: '2rem' }}
                >
                  <Text color="gray.800" fontWeight="600" fontSize={{ base: 'xs', md: 'sm' }}>
                    Booking at Hotel Lake View
                  </Text>
                  <Text color="gray.800" fontWeight="600" fontSize={{ base: 'xs', md: 'sm' }}>
                    Booking at Hotel Lake View that was in Paro, Bhutan.
                  </Text>
                </Flex>

                <CardRow label="Merchant Processing Fee" value="$ 22.11" />
                <CardRow label="Platform Fee" value="$ 00" />
                <CardRow label="Net" value="$ 1122.11" isBold />
              </Box>

              {/* Floating "Invoice" Tab on top right area */}
              <Flex
                position="absolute"
                top={{ base: '20px', md: '40px' }}
                right={{ base: '20px', md: '40px' }}
                bg="white"
                p={{ base: '0.75rem', md: '1rem' }}
                borderRadius="xl"
                boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                direction="column"
                align="center"
                opacity={0}
                animation={
                  isVisible
                    ? `${slideInCustom} 0.6s ease-out forwards 0.8s, ${floatHover15} 5s ease-in-out infinite 1.4s`
                    : 'none'
                }
              >
                <Flex
                  w={{ base: '32px', md: '40px' }}
                  h={{ base: '32px', md: '40px' }}
                  bg="gray.50"
                  borderRadius="md"
                  mb="0.5rem"
                  align="center"
                  justify="center"
                  direction="column"
                  gap="4px"
                >
                  <Box w={{ base: '16px', md: '20px' }} h="2px" bg="gray.200" borderRadius="full" />
                  <Box w={{ base: '16px', md: '20px' }} h="2px" bg="gray.200" borderRadius="full" />
                  <Box
                    w={{ base: '11px', md: '14px' }}
                    h="2px"
                    bg="gray.200"
                    borderRadius="full"
                    alignSelf="flex-start"
                    ml={{ base: '8px', md: '10px' }}
                  />
                </Flex>
                <Flex align="center" gap="4px">
                  <Flex
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="full"
                    w="12px"
                    h="12px"
                    align="center"
                    justify="center"
                  >
                    <Text fontSize="8px" color="gray.400" mt="-1px">
                      ↓
                    </Text>
                  </Flex>
                  <Text fontSize="10px" color="gray.500" fontWeight="700" letterSpacing="0.02em">
                    Invoice
                  </Text>
                </Flex>
              </Flex>

              {/* Floating Checkout Card */}
              <Box
                position="absolute"
                bottom="160px"
                right={{ base: '-20px', md: '-40px' }}
                zIndex="2"
                bg="white"
                p={{ base: '1rem', md: '1.5rem' }}
                borderRadius="2xl"
                boxShadow="0 20px 50px rgba(37, 99, 235, 0.15)"
                w={{ base: '220px', md: '300px' }}
                h={{ base: '130px', md: '160px' }}
                background="linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)"
                border="1px solid"
                borderColor="blue.50"
                opacity={0}
                animation={
                  isVisible
                    ? `${slideInCustom} 0.6s ease-out forwards 1s, ${floatHover15} 6s ease-in-out infinite 1.6s`
                    : 'none'
                }
              >
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap="0.75rem">
                    <Text fontWeight="800" color="blue.900" fontSize={{ base: 'sm', md: 'md' }}>
                      Check Out
                    </Text>
                    <Text color="blue.700" fontSize="lg" fontWeight="300">
                      →
                    </Text>
                  </Flex>
                  <Box
                    w={{ base: '55px', md: '80px' }}
                    h={{ base: '55px', md: '80px' }}
                    filter="drop-shadow(0 4px 6px rgba(0,0,0,0.1))"
                  >
                    <Image src={cartImg} alt="Cart" w="full" h="full" objectFit="contain" />
                  </Box>
                </Flex>
                <Text fontSize="9px" fontWeight="700" color="blue.400" letterSpacing="0.1em">
                  THANK YOU FOR YOUR BUSINESS!
                </Text>
              </Box>
            </Box>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

ServicesSection.displayName = 'ServicesSection';
